import React, { useEffect, useMemo, useState } from 'react';
import {
  getInventoryLogs,
  getOrganizations,
  getPortfolios,
  getProducts
} from '../services/api';

function KpiCard({ label, value, delta, deltaLabel, accent='blue' }){
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-xl shadow-gray-200/50 p-4 md:p-6 transition-all hover:shadow-2xl">
      <div className="text-sm text-gray-600">{label}</div>
      <div className="mt-1 flex items-baseline gap-3">
        <div className="text-3xl md:text-4xl font-extrabold tabular-nums text-gray-900">{value}</div>
        {delta !== undefined && (
          <span className={`text-sm font-medium ${delta < 0 ? 'text-green-600' : 'text-amber-600'}`}>{deltaLabel}</span>
        )}
      </div>
    </div>
  );
}

function TabButton({active, onClick, children}){
  return (
    <button onClick={onClick} className={`px-3 py-2 rounded-lg border transition-all duration-300 ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-200 hover:bg-gray-100'}`}>{children}</button>
  )
}

export default function SustainabilityExplorer(){
  const [orgs, setOrgs] = useState([]);
  const [ports, setPorts] = useState([]);
  const [prods, setProds] = useState([]);
  const [logs, setLogs] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [filterText, setFilterText] = useState('');

  useEffect(()=>{
    getOrganizations().then(setOrgs);
    getPortfolios().then(setPorts);
    getProducts().then(data=>{ setProds(data); if(data?.[0]?.id){ setSelectedProductId(data[0].id); }});
    getInventoryLogs().then(setLogs);
  },[]);

  const selectedProduct = useMemo(()=> prods?.find(p=>p.id===selectedProductId) || null, [prods, selectedProductId]);
  const productLogs = useMemo(()=> logs?.filter(l=>l.product_id===selectedProductId) || [], [logs, selectedProductId]);

  const totals = useMemo(()=>{
    const totalEmissions = productLogs?.reduce((s,l)=>s+(l?.emissions||0),0) || 0;
    const totalCost = productLogs?.reduce((s,l)=>s+(l?.cost||0),0) || 0;
    const lastTwo = productLogs?.slice(-2) || [];
    const delta = lastTwo?.length===2 ? (lastTwo[1].emissions - lastTwo[0].emissions) : 0;
    return { totalEmissions, totalCost, delta };
  },[productLogs]);

  const tree = useMemo(()=>{
    const byOrg = {};
    orgs?.forEach(o=>{ byOrg[o.id] = { ...o, portfolios: [] }; });
    ports?.forEach(p=>{ const o = byOrg[p.organization_id]; if(o){ o.portfolios.push({ ...p, products: [] }); }});
    prods?.forEach(prod=>{
      const p = ports?.find(px=>px.id===prod.portfolio_id);
      const o = p ? byOrg[p.organization_id] : null;
      if(o){
        const tgtPort = o.portfolios.find(pp=>pp.id===p.id);
        if(tgtPort){ tgtPort.products.push(prod); }
      }
    });
    return Object.values(byOrg);
  },[orgs, ports, prods]);

  const filteredTree = useMemo(()=>{
    const q = filterText?.toLowerCase() || '';
    if(!q) return tree;
    return tree?.map(o=>({
      ...o,
      portfolios: o.portfolios?.map(p=>({
        ...p,
        products: p.products?.filter(prod=> (prod?.name||'').toLowerCase().includes(q))
      }))?.filter(p=>p.products?.length>0)
    }))?.filter(o=>o.portfolios?.length>0);
  },[tree, filterText]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* Left rail */}
      <aside className="lg:col-span-4 xl:col-span-3 bg-white border border-gray-200 rounded-xl shadow-xl shadow-gray-200/50 p-4">
        <div className="mb-3">
          <input value={filterText} onChange={e=>setFilterText(e.target.value)} placeholder="Search products…" className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
        </div>
        <div className="space-y-4 max-h-[70vh] overflow-auto pr-2">
          {filteredTree?.map(org=> (
            <div key={org.id}>
              <div className="text-sm font-semibold text-slate-900 mb-2">{org.name}</div>
              {org.portfolios?.map(port=> (
                <div key={port.id} className="ml-2 mb-2">
                  <div className="text-sm text-gray-700 mb-1">{port.name}</div>
                  <div className="space-y-1">
                    {port.products?.map(prod=> (
                      <button
                        key={prod.id}
                        onClick={()=>{ setSelectedProductId(prod.id); setActiveTab('overview'); }}
                        className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition-all ${selectedProductId===prod.id ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-800">{prod.name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full border ${prod.status==='active' ? 'text-green-700 border-green-200 bg-green-50' : prod.status==='beta' ? 'text-amber-700 border-amber-200 bg-amber-50' : 'text-gray-700 border-gray-200 bg-gray-50'}`}>{prod.status}</span>
                        </div>
                        <div className="text-xs text-gray-600">CI: {prod.carbon_intensity} gCO₂e</div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </aside>

      {/* Canvas */}
      <section className="lg:col-span-8 xl:col-span-9 space-y-4">
        <div className="bg-white border border-gray-200 rounded-xl shadow-xl shadow-gray-200/50 p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <div className="text-sm text-gray-600">Selected product</div>
              <div className="text-2xl font-extrabold">{selectedProduct?.name || '—'}</div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <TabButton active={activeTab==='overview'} onClick={()=>setActiveTab('overview')}>Overview</TabButton>
              <TabButton active={activeTab==='recs'} onClick={()=>setActiveTab('recs')}>Recommendations</TabButton>
              <TabButton active={activeTab==='scenarios'} onClick={()=>setActiveTab('scenarios')}>Forecasts & Scenarios</TabButton>
              <TabButton active={activeTab==='code'} onClick={()=>setActiveTab('code')}>Code Insights</TabButton>
            </div>
          </div>
        </div>

        {activeTab==='overview' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <KpiCard label="Total Emissions (kgCO₂e)" value={totals.totalEmissions.toLocaleString()} delta={totals.delta} deltaLabel={`${Math.sign(totals.delta)===-1?'↓':'↑'} ${Math.abs(totals.delta)} vs prev`} />
            <KpiCard label="Total Cost ($)" value={totals.totalCost.toLocaleString()} />
            <KpiCard label="Carbon Intensity" value={`${selectedProduct?.carbon_intensity || 0} gCO₂e`} />
          </div>
        )}

        {activeTab==='recs' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1,2,3].map((i)=>{
              const impact = ["-8% emissions","-12% emissions","-5% emissions"][i-1];
              const title = ["Right-size instances","Enable autoscaling","Turn on lazy-loading"][i-1];
              const [status, setStatus] = useState('new');
              return (
                <div key={i} className="bg-white border border-gray-200 rounded-xl shadow-xl shadow-gray-200/50 p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-semibold text-gray-900">{title}</div>
                      <div className="text-sm text-gray-600">Projected impact: <span className="text-green-600 font-medium">{impact}</span></div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${status==='accepted'?'text-green-700 border-green-200 bg-green-50':status==='rejected'?'text-red-700 border-red-200 bg-red-50':'text-amber-700 border-amber-200 bg-amber-50'}`}>{status}</span>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button onClick={()=>setStatus('accepted')} className="px-3 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-all">Accept</button>
                    <button onClick={()=>setStatus('rejected')} className="px-3 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-all">Reject</button>
                    <button onClick={()=>setStatus('tracking')} className="px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-all">Track</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {activeTab==='scenarios' && (
          <ScenarioPanel product={selectedProduct} />
        )}

        {activeTab==='code' && (
          <div className="bg-white border border-gray-200 rounded-xl shadow-xl shadow-gray-200/50 p-4">
            <div className="font-semibold mb-2">Repositories</div>
            <div className="text-sm text-gray-700">Primary repo: <a className="text-blue-600 hover:underline" href={selectedProduct?.repo_url} target="_blank" rel="noreferrer">{selectedProduct?.repo_url}</a></div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              {["lighthouse checks","unused deps","cpu hotspots","db n+1"].map((r, idx)=> (
                <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-800">Rule: {r}</div>
                    <span className="text-xs px-2 py-0.5 rounded-full border text-amber-700 border-amber-200 bg-amber-50">pending</span>
                  </div>
                  <div className="text-xs text-gray-600 mt-1">No regressions detected in last sync.</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function ScenarioPanel({ product }){
  const [loadFactor, setLoadFactor] = useState(100);
  const base = product?.carbon_intensity || 0;
  const projected = Math.round(base * (loadFactor/100) * 0.9);
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-xl shadow-gray-200/50 p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="text-sm text-gray-600">Assumption: traffic load</div>
          <div className="text-2xl font-extrabold">{loadFactor}%</div>
        </div>
        <input type="range" min="50" max="150" value={loadFactor} onChange={(e)=>setLoadFactor(parseInt(e.target.value))} className="w-full md:w-96" />
      </div>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="text-sm text-gray-600">Current CI</div>
          <div className="text-3xl font-extrabold tabular-nums">{base} gCO₂e</div>
        </div>
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="text-sm text-gray-600">Projected CI</div>
          <div className="text-3xl font-extrabold tabular-nums text-blue-700">{projected} gCO₂e</div>
        </div>
      </div>
    </div>
  );
}
